
import { RoleId } from "./Shared/Shared.js";
import { empty } from "./fable_modules/fable-library-js.5.0.0-rc.2/Map.js";
import { equals, stringHash, compare } from "./fable_modules/fable-library-js.5.0.0-rc.2/Util.js";
import { Permissions_hasAny, Permissions_allowsReceiverType, PermissionScope, Permissions_scopeAtLeast, Permissions_allowsBooking, Permissions_allowsInStorage, Permission, Permissions_has, RolePermissionsDto } from "./Shared/Roles.js";
import { ofArray, collect, isEmpty, filter, contains, exists } from "./fable_modules/fable-library-js.5.0.0-rc.2/List.js";
import { unwrapStorageId } from "./Shared/Helper.js";
import { ReceiverTypeName } from "./Shared/User.js";
import { createContext } from "./fable_modules/Feliz.2.9.0/Internal.fs.js";
import { reactApi } from "./fable_modules/Feliz.2.9.0/Interop.fs.js";
import React from "react";

export const none = new RolePermissionsDto(new RoleId("00000000-0000-0000-0000-000000000000"), "", undefined, empty({
    Compare: (x, y) => (compare(x, y) | 0),
}));

export function canSeeReceivers(permissions) {
    return Permissions_has(permissions, new Permission(41, []));
}

export function canUseGlobalSearch(permissions) {
    if ((Permissions_has(permissions, new Permission(0, [])) ? true : Permissions_has(permissions, new Permission(41, []))) ? true : Permissions_has(permissions, new Permission(65, []))) {
        return true;
    }
    else {
        return Permissions_has(permissions, new Permission(69, []));
    }
}

export function hasSelfServiceAccess(configuration, userId, storages) {
    if (configuration.GlobalSelfServiceStorageActive) {
        return true;
    }
    else {
        return exists((storage) => {
            if (storage.IsSelfService) {
                return true;
            }
            else {
                return contains(userId, storage.AccessByUser, {
                    Equals: (x, y) => (x === y),
                    GetHashCode: (x) => (stringHash(x) | 0),
                });
            }
        }, storages);
    }
}

export function storagesAllowedFor(permissions, permission, userId, storages) {
    return filter((storage) => Permissions_allowsInStorage(permissions, permission, userId, unwrapStorageId(storage.Id), storage.AssignedUserIds), storages);
}

export function allowsInAnyStorageOf(permissions, permission, userId, storages) {
    return !isEmpty(storagesAllowedFor(permissions, permission, userId, storages));
}

export function canBookInStorages(permissions, permission, userId, storages, hasStorageRuleAccess) {
    return Permissions_allowsBooking(permissions, permission, userId, collect((storage) => storage.AssignedUserIds, storages), hasStorageRuleAccess);
}

export function canSeeDashboard(permissions) {
    return Permissions_scopeAtLeast(permissions, new Permission(0, []), new PermissionScope(2, []));
}

export function landingPage(permissions) {
    if (canSeeDashboard(permissions)) {
        return "dashboard";
    }
    else {
        return "tools";
    }
}

export function canReserveFor(permissions, userData, receiver) {
    if (Permissions_has(permissions, new Permission(11, []))) {
        if (Permissions_allowsReceiverType(permissions, new Permission(14, []), receiver.ReceiverType)) {
            return true;
        }
        else if (equals(receiver.ReceiverType, new ReceiverTypeName(0, [])) && (receiver.Id === userData.UserId)) {
            return Permissions_has(permissions, new Permission(15, []));
        }
        else {
            return false;
        }
    }
    else {
        return false;
    }
}

export function isSystemAccount(user) {
    return user.Role === "system";
}

export function canManageUsers(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(53, []), new Permission(54, []), new Permission(55, []), new Permission(56, []), new Permission(57, [])]));
}

export function canManageRoles(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(58, []), new Permission(59, []), new Permission(60, []), new Permission(61, [])]));
}

export function canManageCategories(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(74, []), new Permission(75, []), new Permission(76, [])]));
}

export function canManageCustomFields(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(79, []), new Permission(80, []), new Permission(81, [])]));
}

export function canManageStoragesAndLocations(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(66, []), new Permission(67, []), new Permission(68, []), new Permission(70, []), new Permission(71, []), new Permission(72, [])]));
}

export function canSeeStocktakings(permissions) {
    return Permissions_has(permissions, new Permission(82, []));
}

export function canManageStocktakings(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(83, []), new Permission(84, []), new Permission(85, []), new Permission(87, [])]));
}

export function canCreateStocktakings(permissions) {
    return Permissions_has(permissions, new Permission(83, []));
}

export function canRecordStocktakingResults(permissions) {
    return Permissions_has(permissions, new Permission(86, []));
}

export function canRecordAllStocktakingResults(permissions) {
    return Permissions_scopeAtLeast(permissions, new Permission(86, []), new PermissionScope(4, []));
}

export function canUseCompleteStocktakingScope(permissions) {
    return Permissions_scopeAtLeast(permissions, new Permission(0, []), new PermissionScope(4, []));
}

export function stocktakingStorages(permissions, userId, storages) {
    return storagesAllowedFor(permissions, new Permission(0, []), userId, storages);
}

export function stocktakingReceivers(permissions, userId, receivers) {
    return filter((receiver) => {
        if (receiver.Id === userId) {
            return true;
        }
        else {
            return Permissions_allowsReceiverType(permissions, new Permission(41, []), receiver.ReceiverType);
        }
    }, receivers);
}

export function canManageDispolists(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(99, []), new Permission(100, []), new Permission(101, [])]));
}

export function canManageInspectionTemplates(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(89, []), new Permission(90, []), new Permission(91, [])]));
}

export function canManageProcedureTemplates(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(94, []), new Permission(95, []), new Permission(96, [])]));
}

export function canManageBusinessUnits(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(107, []), new Permission(108, []), new Permission(109, [])]));
}

export function canManageSystemSettings(permissions) {
    return Permissions_has(permissions, new Permission(103, []));
}

export function canManageCustomerData(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(103, []), new Permission(104, [])]));
}

export function canImportOrExport(permissions) {
    return Permissions_hasAny(permissions, ofArray([new Permission(114, []), new Permission(115, []), new Permission(116, []), new Permission(117, []), new Permission(118, [])]));
}

export function canSeeLocations(permissions) {
    return Permissions_has(permissions, new Permission(69, []));
}

export function canSeeTelematics(permissions) {
    return Permissions_has(permissions, new Permission(119, []));
}

export function canSeeAllTrackers(permissions) {
    return Permissions_scopeAtLeast(permissions, new Permission(119, []), new PermissionScope(4, []));
}

export function canAssignTrackers(permissions) {
    return Permissions_has(permissions, new Permission(120, []));
}

export function canManageTelematicsConnection(permissions) {
    return Permissions_has(permissions, new Permission(121, []));
}

const permissionsContext = createContext("UserPermissions", none);

export function usePermissions() {
    return reactApi.useContext(permissionsContext);
}

export function UserPermissionsProvider(userPermissionsProviderInputProps) {
    const children = userPermissionsProviderInputProps.children;
    const permissions = userPermissionsProviderInputProps.permissions;
    return reactApi.createElement(permissionsContext.Provider, {
        value: permissions,
    }, children);
}

